import React, { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { ExtendedCriterion, NewQuota, QuotaRef } from '@app/api';
import CircularProgress from './CircularProgress';
import { ButtonIcon, Chip, Tooltip, TooltipText } from '@app/core';
import { useTranslation } from 'react-i18next';

export const refLabelLimit = 30;
export const quotaLabelLimit = 12;

interface Props {
  criterion: ExtendedCriterion;
  isTagged?: boolean;
  onAddQuotaItemTag?: (criterion: QuotaRef) => any;
  onRemoveQuotaItemTag?: (criterion: QuotaRef) => any;
}

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  criterionContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.marginBase,
  },
  criterionLabel: {
    ...theme.fonts.label,
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase,
    display: 'flex',
    justifyContent: 'space-between',
  },
  quotaContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.marginBase,
    alignItems: 'center',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    justifyContent: 'center',
  },
  progressText: {
    display: 'flex',
    alignItems: 'center',
    marginTop: -1,
    gap: theme.marginBase / 2,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.height.chips,
    width: theme.height.chips,
    fontSize: 28,
  },
  icon: {
    color: theme.colors.green,
  },
  spacer: {
    border: {
      width: 0,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
    borderBottomWidth: 1,
    marginBottom: theme.marginBase * 2,
  },
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: refLabelLimit + 'ch',
  },
  truncatedChipText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: quotaLabelLimit + 'ch',
  },
}));

export const CriterionQuotaBoxLine = ({
  criterion,
  isTagged,
  onAddQuotaItemTag,
  onRemoveQuotaItemTag,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const onHandleTagParticipantButton = useCallback(() => {
    if (!isTagged && onAddQuotaItemTag) {
      onAddQuotaItemTag(criterion);
    } else if (isTagged && onRemoveQuotaItemTag) {
      onRemoveQuotaItemTag(criterion);
    }
  }, [isTagged, onAddQuotaItemTag, onRemoveQuotaItemTag, criterion]);

  const quotas = useMemo(() => {
    return criterion?.quotas?.map((quota: NewQuota) => {
      return {
        ...quota,
        label: quota.label ?? '',
        confirmed: quota.confirmed ?? 0,
        max: quota.max ?? quota.value ?? 0,
      };
    });
  }, [criterion.quotas]);

  return (
    <div key={criterion.id} className={classes.criterionContainer}>
      <div className={classes.criterionLabel}>
        <Tooltip placement="top" arrow={true} title={criterion.fieldLabel}>
          <span className={classes.truncatedText}>{criterion.fieldLabel}</span>
        </Tooltip>
        {onAddQuotaItemTag && (
          <TooltipText
            title={
              isTagged
                ? t('sessions.details.boxes.quota.tooltips.hideTags')
                : t('sessions.details.boxes.quota.tooltips.showTags')
            }
            requireDiv
            placement="top"
          >
            <ButtonIcon
              color={isTagged ? 'blue' : 'ink'}
              colorStyle="muted"
              icon={isTagged ? 'ri-eye-line' : 'ri-eye-off-line'}
              onClick={onHandleTagParticipantButton}
            />
          </TooltipText>
        )}
      </div>
      <div className={classes.spacer} />
      {quotas?.map((quota) => (
        <div className={classes.quotaContainer}>
          <Chip customColor={criterion.color}>
            <Tooltip placement="top" arrow={true} title={quota.label}>
              <span className={classes.truncatedChipText}>{quota.label}</span>
            </Tooltip>
          </Chip>
          <div className={classes.rightSection}>
            <div className={classes.progressText}>
              <strong>{quota.confirmed}</strong>
              <span> / </span>
              {quota.max}
            </div>
            <div className={classes.iconContainer}>
              {quota.value === 0 || quota.confirmed >= quota.max ? (
                <div className={classes.icon}>
                  <i className="ri-checkbox-circle-fill"></i>
                </div>
              ) : (
                <CircularProgress
                  percent={(quota.confirmed / quota.max) * 100}
                />
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
